import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import FieldHelp from '@/components/help/FieldHelp';
import api, { formatApiError } from '@/lib/api';
import { Sparkles, PhoneOff } from 'lucide-react';

// Sprint 36 — voces conocidas y documentadas de OpenAI Realtime. xAI Voice es
// más nuevo/beta, así que ahí se deja un campo libre en vez de una lista fija.
const OPENAI_REALTIME_VOICES = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'marin', 'cedar', 'sage', 'shimmer', 'verse'];
// Voces documentadas / habituales de xAI Voice Agents (Grok). Si xAI agrega más, el tenant puede escribir el id en un input futuro.
const XAI_VOICE_VOICES = ['eve', 'ara', 'rex', 'sal', 'leo'];

// Sprint 53 (1/N) — esta pantalla (Config IA > IA Voice) es SOLO
// configuración desde ahora. El historial/detalle de llamadas vive en
// Conversaciones (Inbox.js): si la llamada coincide con un contacto ya
// conocido aparece como un canal más dentro de esa tarjeta; si no, como su
// propia tarjeta — nunca en una pantalla de historial separada (Sprint 58.1).
export default function VoiceCallCenter() {
  useAuth(); // sesión por cookie; no Bearer

  const [realtimeEnabled, setRealtimeEnabled] = useState(false);
  const [inboundEntitled, setInboundEntitled] = useState(false);
  const [voiceAiProviders, setVoiceAiProviders] = useState([]);
  const [voiceConfig, setVoiceConfig] = useState({ default_voice_ai_provider_key: '', config: {} });
  const [inboundNumbers, setInboundNumbers] = useState([]);
  const [webhookPath, setWebhookPath] = useState('/api/webhooks/voice/inbound');
  const [savingVoiceConfig, setSavingVoiceConfig] = useState(false);
  const [inboundValidateMsg, setInboundValidateMsg] = useState('');
  const [retellStatus, setRetellStatus] = useState(null);
  const [retellAgents, setRetellAgents] = useState([]);
  const [retellNumbers, setRetellNumbers] = useState([]);
  const [retellAgentId, setRetellAgentId] = useState('');
  const [retellPhone, setRetellPhone] = useState('');
  const [retellArea, setRetellArea] = useState('415');
  const [retellCountry, setRetellCountry] = useState('US');
  const [retellBusy, setRetellBusy] = useState(false);
  const [retellMsg, setRetellMsg] = useState('');

  const loadVoiceSettings = useCallback(async () => {
    try {
      const [planRes, providersRes, configRes, numbersRes] = await Promise.all([
        api.get('/tenant/plan'),
        api.get('/tenant/voice/providers', { params: { category: 'voice_ai' } }),
        api.get('/tenant/voice/config'),
        api.get('/tenant/voice/inbound-numbers'),
      ]);
      const plan = planRes.data;
      setRealtimeEnabled(!!plan?.entitlements?.voice_realtime_enabled);
      setInboundEntitled(!!plan?.entitlements?.voice_inbound_enabled);
      setVoiceAiProviders(providersRes.data || []);
      const cfg = configRes.data || {};
      setVoiceConfig({
        default_voice_ai_provider_key: cfg.default_voice_ai_provider_key || '',
        config: cfg.config || {},
      });
      const n = numbersRes.data || {};
      setInboundNumbers(n.numbers || []);
      if (n.webhook_path) setWebhookPath(n.webhook_path);
      try {
        const st = (await api.get('/tenant/voice/retell/status')).data;
        setRetellStatus(st);
        setRetellAgentId(st.agent_id || '');
        setRetellPhone(st.from_number || '');
        if (st.connected) {
          const [ag, nu] = await Promise.all([
            api.get('/tenant/voice/retell/agents'),
            api.get('/tenant/voice/retell/numbers'),
          ]);
          setRetellAgents(ag.data?.agents || []);
          setRetellNumbers(nu.data?.numbers || []);
        }
      } catch { /* retell opcional si no conectado */ }
    } catch { /* carga best-effort */ }
  }, []);

  useEffect(() => { loadVoiceSettings(); }, [loadVoiceSettings]);

  const saveVoiceConfig = async () => {
    setSavingVoiceConfig(true);
    try {
      await api.put('/tenant/voice/config', {
        default_voice_ai_provider_key: voiceConfig.default_voice_ai_provider_key || null,
        config: voiceConfig.config,
      });
      alert('Configuración de voz guardada');
    } catch (err) {
      alert(formatApiError(err?.response?.data?.detail) || 'No se pudo guardar');
    }
    setSavingVoiceConfig(false);
  };


  const refreshRetell = async () => {
    try {
      const st = (await api.get('/tenant/voice/retell/status')).data;
      setRetellStatus(st);
      setRetellAgentId(st.agent_id || '');
      setRetellPhone(st.from_number || '');
      const [ag, nu] = await Promise.all([
        api.get('/tenant/voice/retell/agents'),
        api.get('/tenant/voice/retell/numbers'),
      ]);
      setRetellAgents(ag.data?.agents || []);
      setRetellNumbers(nu.data?.numbers || []);
    } catch {}
  };

  const buyRetellNumber = async () => {
    setRetellBusy(true); setRetellMsg('');
    try {
      const { data } = await api.post('/tenant/voice/retell/numbers/buy', {
        area_code: retellArea,
        country_code: retellCountry,
        agent_id: retellAgentId || undefined,
        nickname: 'CavernicolApp',
        activate: true,
      });
      setRetellMsg(data.message || ('Numero listo: ' + (data.phone_number || '')));
      setRetellPhone(data.phone_number || '');
      await refreshRetell();
      await loadVoiceSettings();
    } catch (err) {
      setRetellMsg(formatApiError(err?.response?.data?.detail) || err?.response?.data?.message || 'No se pudo comprar el numero');
    }
    setRetellBusy(false);
  };

  const activateRetell = async () => {
    setRetellBusy(true); setRetellMsg('');
    try {
      const { data } = await api.post('/tenant/voice/retell/activate', {
        agent_id: retellAgentId,
        phone_number: retellPhone,
        set_as_default_stack: true,
      });
      setRetellMsg(data?.system_tools?.ok === false
          ? ('Retell activado, pero tools: ' + (data.system_tools.error || 'revisa que el agente use Retell LLM'))
          : ('Retell activado con funciones del sistema (agendar, etc.). CRM y automatizaciones pueden llamar.'));
      await refreshRetell();
      await loadVoiceSettings();
    } catch (err) {
      setRetellMsg(formatApiError(err?.response?.data?.detail) || 'No se pudo activar Retell');
    }
    setRetellBusy(false);
  };

  const deactivateRetell = async () => {
    setRetellBusy(true); setRetellMsg('');
    try {
      await api.post('/tenant/voice/retell/deactivate');
      setRetellMsg('Volviste a Twilio + OpenAI/Grok Realtime.');
      await refreshRetell();
      await loadVoiceSettings();
    } catch {
      setRetellMsg('No se pudo desactivar');
    }
    setRetellBusy(false);
  };

  const stackIsRetell = !!retellStatus?.voice_stack_active;

  return (
    <div data-testid="voice-call-center" className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[#0F172A]">IA Voice</h2>
        <p className="text-sm text-[#64748B]">
          Elige <strong>un solo sistema</strong> para que la IA llame y conteste. No mezcles proveedores: todo el negocio (cualquier vertical) usa el mismo camino.
          Historial de llamadas: Conversaciones → Llamadas.
        </p>
      </div>

      {/* Selector único de camino — UX millonaria: 1 decisión, no 3 paneles que se pisan */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="voice-stack-chooser">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-1">¿Cómo quieres la voz de tu negocio?</h3>
        <p className="text-xs text-[#64748B] mb-4">
          Una opción a la vez. Entrantes y salientes del CRM siguen ese mismo camino. Con Retell se usa un solo saludo (entrante si existe; si no, el de voz natural) y el mismo colgado/herramientas que Twilio+OpenAI — sincroniza tras cambiar Config IA.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="voice-path-retell"
            disabled={retellBusy || !retellStatus?.connected || !retellAgentId || !retellPhone}
            onClick={() => { if (!stackIsRetell) activateRetell(); }}
            className={`text-left rounded-xl border p-4 transition-colors ${
              stackIsRetell
                ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200'
                : 'border-[#E2E8F0] hover:border-[#94A3B8] bg-white'
            }`}
          >
            <p className="text-sm font-semibold text-[#0F172A]">Retell (todo en uno)</p>
            <p className="text-[11px] text-[#64748B] mt-1">
              Número + agente en Retell. Al activar se sincronizan: mismas herramientas de voz que Grok/OpenAI + consulta a la Biblioteca mid-call + guion de Config IA. Salientes CRM ON. Factura en tu cuenta Retell.
            </p>
            {stackIsRetell && (
              <p className="text-[11px] font-medium text-emerald-700 mt-2">✓ Activo ahora — salientes y CRM usan Retell</p>
            )}
            {!retellStatus?.connected && (
              <p className="text-[11px] text-amber-700 mt-2">Conecta Retell en Integraciones primero.</p>
            )}
          </button>
          <button
            type="button"
            data-testid="voice-path-twilio"
            disabled={retellBusy}
            onClick={() => { if (stackIsRetell) deactivateRetell(); }}
            className={`text-left rounded-xl border p-4 transition-colors ${
              !stackIsRetell
                ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-slate-300'
                : 'border-[#E2E8F0] hover:border-[#94A3B8] bg-white'
            }`}
          >
            <p className="text-sm font-semibold text-[#0F172A]">Twilio + IA (OpenAI o Grok)</p>
            <p className="text-[11px] text-[#64748B] mt-1">
              Teléfono en Twilio y el “cerebro” de la llamada con OpenAI Realtime o Grok Voice. Un solo stack, no mezclado con Retell.
            </p>
            {!stackIsRetell && (
              <p className="text-[11px] font-medium text-[#0F172A] mt-2">✓ Activo ahora — configura la voz abajo</p>
            )}
          </button>
        </div>
      </div>

      {/* Detalle Retell: solo si eligieron (o quieren preparar) Retell */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="voice-retell-settings">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-1">
          {stackIsRetell ? 'Tu sistema Retell' : 'Preparar Retell (opcional)'}
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          {stackIsRetell
            ? 'Agente y número que usa el CRM. Cambia y pulsa “Usar este número y agente” para aplicar.'
            : 'Si eliges Retell arriba, aquí configuras agente y número. Mientras no lo actives, el negocio sigue en Twilio + IA.'}
        </p>
        {!retellStatus?.connected ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            Primero conecta <strong>Retell AI</strong> en <strong>Integraciones</strong> con tu API Key de dashboard.retellai.com. Luego regresa aqui para comprar el numero.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className={`px-2 py-0.5 rounded-full ${retellStatus.voice_stack_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                {retellStatus.voice_stack_active ? 'Stack activo: Retell' : 'Stack: Twilio / Realtime (Retell opcional)'}
              </span>
              {retellStatus.from_number && <span className="text-slate-500">Numero: {retellStatus.from_number}</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-700">Agente Retell</label>
                <select data-testid="retell-agent-select" className="mt-1 w-full h-9 rounded-md border border-[#E2E8F0] px-2 text-sm" value={retellAgentId} onChange={e => setRetellAgentId(e.target.value)}>
                  <option value="">Selecciona un agente…</option>
                  {retellAgents.map(a => (
                    <option key={a.agent_id} value={a.agent_id}>{a.agent_name || a.agent_id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Numero ya comprado</label>
                <select data-testid="retell-number-select" className="mt-1 w-full h-9 rounded-md border border-[#E2E8F0] px-2 text-sm" value={retellPhone} onChange={e => setRetellPhone(e.target.value)}>
                  <option value="">—</option>
                  {retellNumbers.map(n => (
                    <option key={n.phone_number} value={n.phone_number}>{n.phone_number_pretty || n.phone_number}{n.nickname ? ` (${n.nickname})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="border border-dashed border-[#E2E8F0] rounded-lg p-3">
              <p className="text-xs font-medium text-slate-700 mb-2">Comprar numero nuevo en Retell</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500">Codigo de area</label>
                  <Input data-testid="retell-area-input" value={retellArea} onChange={e => setRetellArea(e.target.value)} placeholder="415" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">Pais</label>
                  <select data-testid="retell-country-select" className="w-full h-9 rounded-md border border-[#E2E8F0] px-2 text-sm" value={retellCountry} onChange={e => setRetellCountry(e.target.value)}>
                    <option value="US">Estados Unidos</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button size="sm" className="w-full" disabled={retellBusy || !retellArea} onClick={buyRetellNumber} data-testid="retell-buy-btn">
                    {retellBusy ? 'Comprando…' : 'Comprar y activar'}
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">La API de Retell vende US/CA. El cargo del numero va a tu cuenta Retell.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={retellBusy || !retellAgentId || !retellPhone} onClick={activateRetell} data-testid="retell-activate-btn">Activar Retell + funciones del sistema</Button>
              {stackIsRetell && (
                <Button size="sm" variant="outline" disabled={retellBusy} onClick={async () => {
                  setRetellBusy(true); setRetellMsg('');
                  try {
                    const { data } = await api.post('/tenant/voice/retell/sync-tools');
                    setRetellMsg('Funciones sincronizadas: ' + ((data.tools || []).join(', ') || 'ok'));
                  } catch (err) {
                    setRetellMsg(formatApiError(err?.response?.data?.detail) || 'No se pudo sincronizar');
                  }
                  setRetellBusy(false);
                }} data-testid="retell-sync-tools-btn">Sincronizar funciones</Button>
              )}
              {stackIsRetell && (
                <Button size="sm" variant="ghost" disabled={retellBusy} onClick={async () => {
                  setRetellBusy(true); setRetellMsg('');
                  try {
                    const { data } = await api.post('/tenant/voice/retell/reconcile');
                    setRetellMsg('Llamadas actualizadas: ' + (data.updated?.length || 0));
                  } catch (err) {
                    setRetellMsg(formatApiError(err?.response?.data?.detail) || 'No se pudo reconciliar');
                  }
                  setRetellBusy(false);
                }} data-testid="retell-reconcile-btn">Actualizar historial de llamadas</Button>
              )}
              {stackIsRetell && (
                <Button size="sm" variant="outline" disabled={retellBusy} onClick={deactivateRetell} data-testid="retell-deactivate-btn">Cambiar a Twilio + IA</Button>
              )}
              <Button size="sm" variant="ghost" disabled={retellBusy} onClick={refreshRetell}>Actualizar lista</Button>
            </div>
            {retellMsg && <p className="text-xs text-slate-600" data-testid="retell-msg">{retellMsg}</p>}
            {retellStatus.webhook_url && (
              <p className="text-[11px] text-slate-400 break-all">Webhook (auto al comprar): {retellStatus.webhook_url}</p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="voice-inbound-settings">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Llamadas entrantes</h3>
        <p className="text-xs text-[#64748B] mb-4">
          Cuando alguien marca el número de tu negocio, la IA puede contestar. Se enruta por el número llamado (To), nunca por el del llamante.
          {!inboundEntitled && ' Tu plan actual no incluye entrantes: pide a soporte activar voice_inbound_enabled.'}
        </p>
        <div className="flex items-center gap-3 mb-4">
          <input
            id="inbound-enabled"
            data-testid="inbound-enabled-toggle"
            type="checkbox"
            className="h-4 w-4"
            checked={voiceConfig.config?.inbound_enabled === true}
            disabled={!inboundEntitled}
            onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, inbound_enabled: e.target.checked } }))}
          />
          <label htmlFor="inbound-enabled" className="text-sm text-[#0F172A]">Activar llamadas entrantes con IA</label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldHelp
            label="Números disponibles (enmascarados)"
            htmlFor="inbound-numbers"
            help="Solo ves números ya asignados a tu negocio en Integraciones o en el pool de plataforma. Si está vacío, conecta Twilio Voice primero."
          >
            <div id="inbound-numbers" data-testid="inbound-numbers-list" className="text-xs text-[#475569] space-y-1 border border-[#E2E8F0] rounded-md p-2 min-h-[2.5rem]">
              {inboundNumbers.length === 0 && <span className="text-[#94A3B8]">Sin números de voz asignados</span>}
              {inboundNumbers.map(n => (
                <div key={n.id}>{n.identity_masked} · {n.provider_key} · {n.status || '—'}</div>
              ))}
            </div>
          </FieldHelp>
          <FieldHelp
            label="URL de llamadas entrantes (solo si activas entrantes arriba)"
            htmlFor="inbound-webhook"
            help="NO se usa para el puente lead↔vendedor (eso solo necesita Twilio en Integraciones). Solo si alguien marca tu número y quieres que conteste la IA: en Twilio Inventory → detalle del número → Voice → A call comes in → Webhook HTTP POST, pega la URL completa."
          >
            <div className="flex gap-2">
              <Input
                id="inbound-webhook"
                data-testid="inbound-webhook-path"
                readOnly
                value={typeof window !== 'undefined' ? `${window.location.origin}${webhookPath}` : webhookPath}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => {
                  const full = `${window.location.origin}${webhookPath}`;
                  navigator.clipboard.writeText(full);
                  toast.success('URL de entrantes copiada');
                }}
                data-testid="copy-inbound-webhook"
              >
                Copiar
              </Button>
            </div>
            <p className="text-[11px] text-[#64748B] mt-1.5">
              Puente y salientes: credenciales en <a className="underline font-medium text-[#F72585]" href="/tenant/integrations?provider=twilio_voice">Integraciones → Twilio Voice</a>.
              Esta URL solo si activas «Llamadas entrantes con IA».
            </p>
          </FieldHelp>
          <FieldHelp
            label="Saludo entrante"
            htmlFor="inbound-greeting"
            help="Lo primero que dice la IA al contestar una llamada entrante. Es independiente del saludo de llamadas salientes."
          >
            <Input
              id="inbound-greeting"
              data-testid="inbound-greeting-input"
              placeholder="Hola, gracias por llamar a…"
              value={voiceConfig.config?.inbound_greeting || ''}
              onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, inbound_greeting: e.target.value } }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Objetivo del agente (entrantes)"
            htmlFor="inbound-objective"
            help="Qué debe lograr la IA en una llamada que entra (agendar, calificar, transferir, etc.)."
          >
            <Input
              id="inbound-objective"
              data-testid="inbound-objective-input"
              placeholder="Agendar cita o contestar dudas de precios"
              value={voiceConfig.config?.inbound_objective || ''}
              onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, inbound_objective: e.target.value } }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Contexto entrante"
            htmlFor="inbound-context"
            help="Detalles del negocio solo para quien llama entrante (promos, horarios, lo que no debe prometer)."
          >
            <Textarea
              id="inbound-context"
              data-testid="inbound-context-input"
              rows={2}
              value={voiceConfig.config?.inbound_context || ''}
              onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, inbound_context: e.target.value } }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Mensaje si la IA no puede atender"
            htmlFor="inbound-fallback"
            help="Se usa si las entrantes están desactivadas o hay un problema de configuración."
          >
            <Input
              id="inbound-fallback"
              data-testid="inbound-fallback-input"
              placeholder="Gracias por llamar, intenta más tarde."
              value={voiceConfig.config?.inbound_fallback_message || ''}
              onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, inbound_fallback_message: e.target.value } }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Horario inicio (HH:MM)"
            htmlFor="inbound-hours-start"
            help="Opcional. Si activas horario, fuera de esta ventana se reproduce el mensaje fuera de horario."
          >
            <Input
              id="inbound-hours-start"
              data-testid="inbound-hours-start"
              placeholder="09:00"
              value={voiceConfig.config?.inbound_hours?.start || ''}
              onChange={e => setVoiceConfig(v => ({
                ...v,
                config: {
                  ...v.config,
                  inbound_hours: { ...(v.config?.inbound_hours || {}), enabled: true, start: e.target.value, timezone: v.config?.inbound_hours?.timezone || 'America/Mexico_City' },
                },
              }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Horario fin (HH:MM)"
            htmlFor="inbound-hours-end"
            help="Hora local de cierre del horario de atención entrante."
          >
            <Input
              id="inbound-hours-end"
              data-testid="inbound-hours-end"
              placeholder="18:00"
              value={voiceConfig.config?.inbound_hours?.end || ''}
              onChange={e => setVoiceConfig(v => ({
                ...v,
                config: {
                  ...v.config,
                  inbound_hours: { ...(v.config?.inbound_hours || {}), enabled: true, end: e.target.value },
                },
              }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Mensaje fuera de horario"
            htmlFor="inbound-outside-hours"
            help="Lo que escucha quien llama fuera del horario configurado."
          >
            <Input
              id="inbound-outside-hours"
              data-testid="inbound-outside-hours"
              value={voiceConfig.config?.inbound_outside_hours_message || ''}
              onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, inbound_outside_hours_message: e.target.value } }))}
            />
          </FieldHelp>
          <FieldHelp
            label="Duración máxima (segundos)"
            htmlFor="inbound-max-duration"
            help="Tope opcional de la conversación entrante. 0 o vacío = sin tope en esta capa."
          >
            <Input
              id="inbound-max-duration"
              data-testid="inbound-max-duration"
              type="number"
              min="0"
              value={voiceConfig.config?.inbound_max_duration_seconds ?? ''}
              onChange={e => setVoiceConfig(v => ({
                ...v,
                config: { ...v.config, inbound_max_duration_seconds: e.target.value === '' ? '' : Number(e.target.value) },
              }))}
            />
          </FieldHelp>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <p className="text-[11px] text-[#94A3B8]">La prueba no origina una llamada real. Valida configuración y el webhook.</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="inbound-validate-btn"
              onClick={async () => {
                try {
                  const j = (await api.post('/tenant/voice/inbound/validate')).data || {};
                  setInboundValidateMsg(j.message || (j.ok ? 'OK' : (j.issues || []).join(' · ')));
                } catch {
                  setInboundValidateMsg('No se pudo validar');
                }
              }}
            >
              Probar configuración
            </Button>
            <Button size="sm" onClick={saveVoiceConfig} disabled={savingVoiceConfig} data-testid="save-voice-inbound-config-btn">
              {savingVoiceConfig ? 'Guardando...' : 'Guardar entrantes'}
            </Button>
          </div>
        </div>
        {inboundValidateMsg && (
          <p className="text-xs text-[#475569] mt-2" data-testid="inbound-validate-msg">{inboundValidateMsg}</p>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="voice-outbound-settings">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Llamadas salientes</h3>
        <p className="text-xs text-[#64748B] mb-4">
          Para no ser invasivos, el sistema evita llamar dos veces al mismo número en poco tiempo. Aquí puedes ajustar cuánto tiempo espera.
        </p>
        {stackIsRetell ? (
          <p className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2 mb-4" data-testid="outbound-stack-banner">
            <strong>Retell + funciones del sistema (S121).</strong> Salientes del CRM y automatizaciones
            marcan con el agente Retell; mid-call puede agendar y usar tools de CavernicolApp.
            Tras cambiar saludo/herramientas en Config IA, pulsa <strong>Activar Retell</strong> otra vez
            o «Sincronizar funciones» para empujar el guion al agente.
          </p>
        ) : (
          <p className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2 mb-4" data-testid="outbound-stack-banner">
            <strong>Twilio + IA activos</strong> (CRM, automatizaciones, «Llamar con IA») con OpenAI/Grok.
            Misma regla en todas las verticales.
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldHelp
            label="Espera entre llamadas al mismo número (horas)"
            htmlFor="repeat-call-cooldown"
            help="Si a alguien ya se le llamó con IA, el sistema no lo vuelve a llamar hasta que pase este tiempo — evita que un lead reciba varias llamadas seguidas por error. Ponlo en 0 solo si estás haciendo pruebas tú mismo."
          >
            <Input
              id="repeat-call-cooldown"
              data-testid="repeat-call-cooldown-input"
              type="number"
              min="0"
              step="0.5"
              value={voiceConfig.config?.repeat_call_cooldown_hours ?? 4}
              onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, repeat_call_cooldown_hours: e.target.value === '' ? '' : Number(e.target.value) } }))}
            />
          </FieldHelp>
        </div>
      </div>

      {realtimeEnabled && !stackIsRetell && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="voice-realtime-settings">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#7C3AED]" />
            <h3 className="text-sm font-semibold text-[#0F172A]">Voz natural en tiempo real</h3>
          </div>
          <p className="text-xs text-[#64748B] mb-4">
            Cerebro de la llamada con Twilio: OpenAI Realtime o Grok Voice. Conversación natural (escucha y habla a la vez).
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldHelp
              label="Quién habla por la IA"
              htmlFor="voice-realtime-provider"
              help="OpenAI Realtime o Grok Voice (xAI). El teléfono es Twilio; aquí solo eliges la voz/cerebro."
            >
              <select
                id="voice-realtime-provider"
                data-testid="voice-realtime-provider-select"
                className="w-full h-9 rounded-md border border-[#E2E8F0] px-2 text-sm"
                value={voiceConfig.default_voice_ai_provider_key}
                onChange={e => setVoiceConfig(v => ({ ...v, default_voice_ai_provider_key: e.target.value, config: { ...v.config, realtime_voice_id: '' } }))}
              >
                <option value="">Sin configurar (usa la voz por turnos, menos natural)</option>
                <option value="openai_realtime">OpenAI Realtime</option>
                <option value="xai_voice">Grok Voice (xAI)</option>
              </select>
              {(() => {
                const p = voiceAiProviders.find(p => p.key === voiceConfig.default_voice_ai_provider_key);
                if (!voiceConfig.default_voice_ai_provider_key || !p) return null;
                if (p.connection_status !== 'configured') {
                  const hint = voiceConfig.default_voice_ai_provider_key === 'xai_voice'
                    ? 'Conecta xAI (Grok) o "xAI Voice (Grok)" en Integraciones con tu API key de console.x.ai. También sirve la misma key del proveedor xAI de chat.'
                    : 'Aún no conectas tus credenciales de este proveedor en Integraciones — sin eso, las llamadas no podrán usar esta voz.';
                  return <p className="text-[11px] text-amber-600 mt-1">{hint}</p>;
                }
                return null;
              })()}
            </FieldHelp>

            <FieldHelp
              label="Voz"
              htmlFor="voice-realtime-voice-id"
              help="El tono/timbre con el que va a sonar tu asistente. Puedes probar varias y quedarte con la que más te guste."
            >
              {voiceConfig.default_voice_ai_provider_key === 'openai_realtime' ? (
                <select
                  id="voice-realtime-voice-id"
                  data-testid="voice-realtime-voice-select"
                  className="w-full h-9 rounded-md border border-[#E2E8F0] px-2 text-sm"
                  value={voiceConfig.config?.realtime_voice_id || ''}
                  onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, realtime_voice_id: e.target.value } }))}
                >
                  <option value="">Voz por defecto</option>
                  {OPENAI_REALTIME_VOICES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : voiceConfig.default_voice_ai_provider_key === 'xai_voice' ? (
                <select
                  id="voice-realtime-voice-id"
                  data-testid="voice-realtime-xai-voice-select"
                  className="w-full h-9 rounded-md border border-[#E2E8F0] px-2 text-sm"
                  value={voiceConfig.config?.realtime_voice_id || ''}
                  onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, realtime_voice_id: e.target.value } }))}
                >
                  <option value="">Voz por defecto (eve)</option>
                  {XAI_VOICE_VOICES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <Input
                  id="voice-realtime-voice-id"
                  data-testid="voice-realtime-voice-input"
                  placeholder="elije un proveedor primero"
                  disabled
                  value={voiceConfig.config?.realtime_voice_id || ''}
                />
              )}
            </FieldHelp>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <FieldHelp
              label="Saludo inicial"
              htmlFor="voice-realtime-greeting"
              help='Lo primero que dice tu asistente apenas la persona contesta — antes esperaba en silencio a ver qué decía el lead ("bueno", "alo"), lo cual hacía que la llamada se sintiera muerta. Ahora siempre saluda primero con este texto.'
            >
              <Input
                id="voice-realtime-greeting"
                data-testid="voice-realtime-greeting-input"
                placeholder="Hola, ¿en qué te puedo ayudar hoy?"
                value={voiceConfig.config?.realtime_greeting || ''}
                onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, realtime_greeting: e.target.value } }))}
              />
            </FieldHelp>
            <FieldHelp
              label="Mensaje al consultar disponibilidad"
              htmlFor="voice-booking-hold"
              help="Cuando la IA está agendando una cita, el sistema consulta la agenda de verdad. Ese mensaje se dice en voz alta mientras se consulta, para que no haya silencio. No inventa disponibilidad: solo rellena la espera."
            >
              <Input
                id="voice-booking-hold"
                data-testid="voice-booking-hold-input"
                placeholder="Un momento, estoy consultando la disponibilidad en la agenda."
                value={voiceConfig.config?.booking_hold_message || ''}
                onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, booking_hold_message: e.target.value } }))}
              />
            </FieldHelp>
            <FieldHelp
              label="Contexto adicional del negocio"
              htmlFor="voice-realtime-context"
              help="Cualquier detalle que tu asistente deba tener siempre presente en la llamada — horarios, promociones vigentes, cosas que NO debe prometer, etc. Se suma a la configuración general que ya tienes en Config IA."
            >
              <Textarea
                id="voice-realtime-context"
                data-testid="voice-realtime-context-input"
                rows={2}
                placeholder="Ej. Solo agendamos citas de martes a sábado, de 10am a 6pm."
                value={voiceConfig.config?.realtime_context || ''}
                onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, realtime_context: e.target.value } }))}
              />
            </FieldHelp>
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-[11px] text-[#94A3B8]">Este tipo de llamada cuesta más por minuto que las llamadas normales — puedes ver el detalle en Config IA → Costos.</p>
            <Button size="sm" onClick={saveVoiceConfig} disabled={savingVoiceConfig} data-testid="save-voice-realtime-config-btn">
              {savingVoiceConfig ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      )}

      {/* Sprint 53 (2/N) — movido debajo del saludo inicial (dentro del
          bloque de voz en tiempo real de arriba) por pedido explícito del
          usuario; aplica a ambas vías de llamada (por turnos y tiempo real). */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="voice-farewell-settings">
        <div className="flex items-center gap-2 mb-1">
          <PhoneOff className="w-4 h-4 text-[#64748B]" />
          <h3 className="text-sm font-semibold text-[#0F172A]">Mensaje de despedida</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-4">
          Lo último que dice la IA antes de colgar, cuando la persona confirma que ya no necesita nada más. Si lo dejas vacío, se usa un mensaje genérico de cortesía.
        </p>
        <FieldHelp
          label="Mensaje de despedida"
          htmlFor="voice-farewell-message"
          help="La IA lo adapta naturalmente a la conversación, no lo repite palabra por palabra como un robot. En la llamada en tiempo real, el sistema espera a que termine de sonar la despedida completa antes de colgar de verdad."
        >
          <Textarea
            id="voice-farewell-message"
            data-testid="voice-farewell-message-input"
            rows={2}
            placeholder="Fue un placer atenderte, que tengas un excelente día."
            value={voiceConfig.config?.voice_farewell_message || ''}
            onChange={e => setVoiceConfig(v => ({ ...v, config: { ...v.config, voice_farewell_message: e.target.value } }))}
          />
        </FieldHelp>
        <div className="flex justify-end mt-4">
          <Button size="sm" onClick={saveVoiceConfig} disabled={savingVoiceConfig} data-testid="save-voice-outbound-config-btn">
            {savingVoiceConfig ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
